import React, { useState } from "react";
import { AmplifyMessageMap } from "aws-amplify-react/lib/AmplifyMessageMap";
import { Button, Input } from "@/ui";
import { Icon } from "base/components";
import { applyPostAuthState } from "auth/services";
import { AuthShell } from "./AuthShell";
import formStyles from "./AuthForm.module.scss";

/** Shared props Amplify injects into AuthPiece children. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AmplifyAuthPieceProps = any;

export type AuthFormErrorProps = {
    message?: string | null;
};

/**
 * Form-level auth error — DS Alert Messages pattern (inline status alert).
 * `Message` is empty-state anatomy; `MessageBanner` is the dark promo bar.
 */
export const AuthFormError = ({ message }: AuthFormErrorProps) => {
    if (!message) {
        return null;
    }

    return (
        <div
            aria-live="assertive"
            className={formStyles.formError}
            role="alert"
        >
            <span aria-hidden className={formStyles.formErrorIcon}>
                <Icon icon="error" />
            </span>
            <p className={formStyles.formErrorText}>{message}</p>
        </div>
    );
};

/**
 * Mix into Amplify AuthPiece subclasses: store errors in `state.authError`
 * and skip Amplify's Toast (errors render via `AuthFormError` instead).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function withInlineAuthError(AmplifyBase: any) {
    return class extends AmplifyBase {
        constructor(props: unknown) {
            super(props);
            this.state = {
                ...(this.state || {}),
                authError: this.state?.authError ?? null,
            };
        }

        error(err: unknown) {
            const raw = this.errorMessage(err);
            const map = this.props.messageMap || AmplifyMessageMap;
            const message =
                typeof map === "string"
                    ? map
                    : typeof map === "function"
                    ? map(raw)
                    : raw;
            this.setState({ authError: message });
        }

        handleInputChange(evt: React.ChangeEvent<HTMLInputElement>) {
            if (this.state?.authError) {
                this.setState({ authError: null });
            }
            AmplifyBase.prototype.handleInputChange.call(this, evt);
        }

        checkContact(user: unknown) {
            applyPostAuthState(
                user,
                (state, data) => this.changeState(state, data),
                {
                    setLoading: loading => this.setState({ loading }),
                },
            );
        }
    };
}

export type AuthTextButtonProps = {
    children: React.ReactNode;
    onClick: () => void;
    tone?: "blue" | "green" | "muted";
    "data-test"?: string;
};

export const AuthTextButton = ({
    children,
    onClick,
    tone = "green",
    ...rest
}: AuthTextButtonProps) => {
    const className =
        tone === "blue"
            ? formStyles.linkBlue
            : tone === "muted"
            ? formStyles.linkMuted
            : formStyles.linkGreen;

    return (
        <button
            className={className}
            onClick={e => {
                e.preventDefault();
                onClick();
            }}
            type="button"
            {...rest}
        >
            {children}
        </button>
    );
};

export type AuthUsernameFieldProps = {
    label: string;
    name?: string;
    type?: string;
    defaultValue?: string;
    disabled?: boolean;
    autoFocus?: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    "data-test"?: string;
};

export const AuthUsernameField = ({
    label,
    name = "username",
    type = "text",
    defaultValue,
    disabled,
    autoFocus,
    onChange,
    ...rest
}: AuthUsernameFieldProps) => (
    <Input
        autoFocus={autoFocus}
        className={formStyles.field}
        defaultValue={defaultValue}
        disabled={disabled}
        fullWidth
        label={label}
        name={name}
        onChange={onChange}
        type={type}
        {...rest}
    />
);

export type AuthPasswordFieldProps = {
    label: string;
    name?: string;
    autoFocus?: boolean;
    autoComplete?: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    "data-test"?: string;
};

/** Password field with show/hide toggle (eye / eye-off). */
export const AuthPasswordField = ({
    label,
    name = "password",
    autoFocus,
    autoComplete = "current-password",
    onChange,
    ...rest
}: AuthPasswordFieldProps) => {
    const [visible, setVisible] = useState(false);

    return (
        <Input
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            className={formStyles.field}
            fullWidth
            iconRight={
                <button
                    aria-label={visible ? "Hide password" : "Show password"}
                    aria-pressed={visible}
                    className={formStyles.revealButton}
                    onClick={() => setVisible(v => !v)}
                    onMouseDown={e => e.preventDefault()}
                    type="button"
                >
                    <Icon icon={visible ? "eye-off" : "eye"} />
                </button>
            }
            label={label}
            name={name}
            onChange={onChange}
            type={visible ? "text" : "password"}
            {...rest}
        />
    );
};

export type AuthSubmitButtonProps = {
    children: React.ReactNode;
    loading?: boolean;
    disabled?: boolean;
    type?: "button" | "submit";
    onClick?: () => void;
    "data-test"?: string;
};

export const AuthSubmitButton = ({
    children,
    loading,
    disabled,
    type = "submit",
    onClick,
    ...rest
}: AuthSubmitButtonProps) => (
    <Button
        className={formStyles.submit}
        disabled={disabled}
        fullWidth
        loading={loading}
        onClick={onClick}
        size="lg"
        type={type}
        variant="primary"
        {...rest}
    >
        {children}
    </Button>
);

export { AuthShell, formStyles };
